import { useMemo } from "react";

import * as Repository from "../repository";
import { useClickRepeaterReservation } from "./use-click-repeater-reservation";
import {
	useUpdateReservationDetailCancel,
	useUpdateReservationDetailChargeCancelFee,
	useUpdateReservationDetailReservationRequestHandling,
	useUpdateReservationDetailUpdateEmailResendRequest,
	useUpdateReservationDetailUpdateHostMemo,
	useUpdateReservationDetailIssueReceipt,
	useUpdateReservationDetailUpdateIssuedReceiptStatus,
	useUpdateReservationDetailUpdateMailMagazineStatus,
	useUpdateReservationDetailUpdateNoShowStatus,
	useUpdateReservationDetailUpdateRefundRequest,
	useUpdateReservationLocalCancelFeeTransferAccept,
	useUpdateReservationLocalCancelFeeTransferReject,
} from "./use-update";

import type { QueryData } from "../type";

export const useInitialized = (hotelID: string, reservationID: string) => {
	/**
	 * Query の実行
	 */
	const { reservationDetailForManagementReservationItemServerModel, isHostDirectReservationAvailable } =
		Repository.Query.useReservationDetailForManagementReservationItemQuery(hotelID, reservationID);

	/**
	 * QueryData の生成
	 */
	const queryData = useMemo((): QueryData | undefined => {
		if (!reservationDetailForManagementReservationItemServerModel) return undefined;
		return {
			...reservationDetailForManagementReservationItemServerModel.reservationDetail,
			repeaterReservationList: reservationDetailForManagementReservationItemServerModel.repeaterReservationList,
			isHostDirectReservationAvailable,
		} satisfies QueryData;
	}, [reservationDetailForManagementReservationItemServerModel, isHostDirectReservationAvailable]);

	/**
	 * Mutation の生成
	 */
	// リクエスト予約のステータス更新
	const {
		onUpdateReservationDetailReservationRequestHandlingAccept,
		onUpdateReservationDetailReservationRequestHandlingReject,
	} = useUpdateReservationDetailReservationRequestHandling(hotelID, reservationID);
	// キャンセルの実行
	const { onUpdateReservationDetailCancel } = useUpdateReservationDetailCancel(hotelID, reservationID);
	// キャンセル料の請求（与信失敗による強制キャンセル後）
	const { onChargeCancelFee } = useUpdateReservationDetailChargeCancelFee(hotelID, reservationID);
	// 返金処理の更新
	const { onUpdateReservationDetailUpdateRefundRequest } = useUpdateReservationDetailUpdateRefundRequest(
		hotelID,
		reservationID,
	);
	// 現地決済のキャンセル料金支払いを承認する
	const { onUpdateReservationLocalCancelFeeTransferAccept } = useUpdateReservationLocalCancelFeeTransferAccept(
		hotelID,
		reservationID,
	);
	// 現地決済のキャンセル料金支払いを非承認する
	const { onUpdateReservationLocalCancelFeeTransferReject } = useUpdateReservationLocalCancelFeeTransferReject(
		hotelID,
		reservationID,
	);
	// お客様のステータスを更新
	const { onUpdateReservationDetailUpdateNoShow } = useUpdateReservationDetailUpdateNoShowStatus(
		hotelID,
		reservationID,
	);
	// 領収書の発行
	const { onIssueReceipt } = useUpdateReservationDetailIssueReceipt(hotelID, reservationID);
	// 領収書のステータスを更新
	const { onUpdateReservationDetailUpdateIssuedReceiptStatus } = useUpdateReservationDetailUpdateIssuedReceiptStatus(
		hotelID,
		reservationID,
	);
	// メアドの更新、再送信
	const { onUpdateReservationDetailUpdateMailResendRequest } = useUpdateReservationDetailUpdateEmailResendRequest(
		hotelID,
		reservationID,
	);
	// メルマガ希望の更新
	const { onUpdateReservationDetailUpdateMailMagazineStatus } = useUpdateReservationDetailUpdateMailMagazineStatus(
		hotelID,
		reservationID,
	);
	// ホストメモの更新
	const { onUpdateReservationDetailUpdateHostMemo } = useUpdateReservationDetailUpdateHostMemo(
		hotelID,
		reservationID,
	);
	// 過去予約履歴をクリックしたときの処理
	const { onClickRepeaterReservation } = useClickRepeaterReservation();

	return {
		//
		queryData,

		// mutation
		// リクエスト予約を承認する
		onUpdateReservationDetailReservationRequestHandlingAccept,
		// リクエスト予約を非承認する
		onUpdateReservationDetailReservationRequestHandlingReject,
		// キャンセルの実行
		onUpdateReservationDetailCancel,
		onChargeCancelFee,
		// 返金処理の更新
		onUpdateReservationDetailUpdateRefundRequest,
		// 現地決済のキャンセル料金支払いを承認する
		onUpdateReservationLocalCancelFeeTransferAccept,
		// 現地決済のキャンセル料金支払いを非承認する
		onUpdateReservationLocalCancelFeeTransferReject,
		// お客様のステータスを更新
		onUpdateReservationDetailUpdateNoShow,
		// 領収書の発行
		onIssueReceipt,
		// 領収書のステータスを更新
		onUpdateReservationDetailUpdateIssuedReceiptStatus,
		// メアドの更新、再送信
		onUpdateReservationDetailUpdateMailResendRequest,
		// メルマガ希望の更新
		onUpdateReservationDetailUpdateMailMagazineStatus,
		// ホストメモの更新
		onUpdateReservationDetailUpdateHostMemo,
		// 過去予約履歴をクリックしたときの処理
		onClickRepeaterReservation,
	};
};
